import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// Shared pagination type
// ---------------------------------------------------------------------------

export interface Pagination {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
  readonly total_pages?: number;
}

// ---------------------------------------------------------------------------
// B2B Customer types
// ---------------------------------------------------------------------------

export type B2BCustomerStatus = "active" | "suspended" | "pending_approval";

export interface B2BCustomer {
  readonly id: string;
  readonly company_name: string;
  readonly contact_name: string;
  readonly email: string;
  readonly phone: string;
  readonly region: string;
  readonly credit_limit_fcfa: number;
  readonly credit_used_fcfa: number;
  readonly credit_available_fcfa: number;
  readonly payment_terms_days: number;
  readonly status: B2BCustomerStatus;
  readonly total_orders: number;
  readonly total_spend_fcfa: number;
  readonly created_at: string;
  readonly assigned_rep_id: string | null;
  readonly assigned_rep_name: string | null;
}

export interface B2BCustomerListResponse {
  readonly customers: readonly B2BCustomer[];
  readonly pagination: Pagination;
}

export interface B2BCustomerListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly search?: string | null;
  readonly region?: string | null;
  readonly status?: B2BCustomerStatus | null;
}

export interface B2BCustomerStats {
  readonly total: number;
  readonly active: number;
  readonly suspended: number;
  readonly pending: number;
  readonly total_credit_used_fcfa: number;
  readonly total_credit_limit_fcfa: number;
}

export interface CreateB2BCustomerInput {
  readonly company_name: string;
  readonly contact_name: string;
  readonly email: string;
  readonly phone: string;
  readonly region: string;
  readonly credit_limit_fcfa: number;
  readonly payment_terms_days: number;
  readonly assigned_rep_id?: string;
}

export type UpdateB2BCustomerInput = Partial<CreateB2BCustomerInput>;

// ---------------------------------------------------------------------------
// Price list types
// ---------------------------------------------------------------------------

export interface PriceList {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly customer_group: string;
  readonly discount_percent: number;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly product_count: number;
}

export interface PriceListItem {
  readonly id: string;
  readonly price_list_id: string;
  readonly product_id: string;
  readonly product_name: string;
  readonly sku: string;
  readonly base_price_fcfa: number;
  readonly list_price_fcfa: number;
  readonly discount_percent: number;
}

export interface PriceListListResponse {
  readonly price_lists: readonly PriceList[];
  readonly pagination: Pagination;
}

export interface PriceListItemsResponse {
  readonly items: readonly PriceListItem[];
}

export interface PriceListListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly search?: string | null;
}

export interface CreatePriceListInput {
  readonly name: string;
  readonly description: string;
  readonly customer_group: string;
  readonly discount_percent: number;
}

// ---------------------------------------------------------------------------
// Credit application types
// ---------------------------------------------------------------------------

export type CreditApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "under_review";

export interface CreditApplication {
  readonly id: string;
  readonly customer_id: string;
  readonly company_name: string;
  readonly requested_amount_fcfa: number;
  readonly status: CreditApplicationStatus;
  readonly submitted_at: string;
  readonly reviewed_at: string | null;
  readonly reviewed_by: string | null;
  readonly notes: string | null;
}

export interface CreditListResponse {
  readonly applications: readonly CreditApplication[];
  readonly pagination: Pagination;
}

export interface CreditListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: CreditApplicationStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

// ---------------------------------------------------------------------------
// Invoice types
// ---------------------------------------------------------------------------

export type B2BInvoiceStatus =
  | "draft"
  | "sent"
  | "overdue"
  | "paid"
  | "cancelled";

export interface B2BInvoice {
  readonly id: string;
  readonly invoice_number: string;
  readonly customer_id: string;
  readonly company_name: string;
  readonly amount_fcfa: number;
  readonly status: B2BInvoiceStatus;
  readonly issued_at: string;
  readonly due_at: string;
  readonly paid_at: string | null;
}

export interface B2BInvoiceListResponse {
  readonly invoices: readonly B2BInvoice[];
  readonly pagination: Pagination;
}

export interface B2BInvoiceListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: B2BInvoiceStatus | null;
  readonly customer_id?: string | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

// ---------------------------------------------------------------------------
// Aging report types
// ---------------------------------------------------------------------------

export type AgingBucketLabel = "0-30j" | "31-60j" | "61-90j" | ">90j";

export interface AgingBucket {
  readonly label: AgingBucketLabel;
  readonly amount_fcfa: number;
  readonly invoice_count: number;
  readonly customer_count: number;
}

export interface AgingCustomerRow {
  readonly customer_id: string;
  readonly company_name: string;
  readonly amount_fcfa: number;
  readonly oldest_invoice_days: number;
  readonly bucket_0_30: number;
  readonly bucket_31_60: number;
  readonly bucket_61_90: number;
  readonly bucket_over_90: number;
}

export interface AgingReport {
  readonly total_overdue_fcfa: number;
  readonly buckets: readonly AgingBucket[];
  readonly customers: readonly AgingCustomerRow[];
}

// ---------------------------------------------------------------------------
// Sales rep types
// ---------------------------------------------------------------------------

export interface SalesRep {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly phone: string;
  readonly region: string;
  readonly customer_count: number;
  readonly monthly_target_fcfa: number;
  readonly monthly_actual_fcfa: number;
  readonly active: boolean;
}

export interface SalesRepListResponse {
  readonly reps: readonly SalesRep[];
  readonly pagination: Pagination;
}

export interface SalesRepListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly search?: string | null;
  readonly region?: string | null;
}

export interface CreateSalesRepInput {
  readonly name: string;
  readonly email: string;
  readonly phone: string;
  readonly region: string;
  readonly monthly_target_fcfa: number;
  readonly active: boolean;
}

// ---------------------------------------------------------------------------
// Supplier types
// ---------------------------------------------------------------------------

export type SupplierStatus = "active" | "inactive";

export interface Supplier {
  readonly id: string;
  readonly name: string;
  readonly contact: string;
  readonly phone: string;
  readonly email: string;
  readonly region: string;
  readonly product_count: number;
  readonly last_order_at: string | null;
  readonly status: SupplierStatus;
}

export interface SupplierListResponse {
  readonly suppliers: readonly Supplier[];
  readonly pagination: Pagination;
}

export interface SupplierListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly search?: string | null;
  readonly status?: SupplierStatus | null;
  readonly region?: string | null;
}

export interface CreateSupplierInput {
  readonly name: string;
  readonly contact: string;
  readonly phone: string;
  readonly email: string;
  readonly region: string;
  readonly status: SupplierStatus;
}

// ---------------------------------------------------------------------------
// Volume discount types
// ---------------------------------------------------------------------------

export interface VolumeDiscount {
  readonly id: string;
  readonly name: string;
  readonly product_id: string;
  readonly product_name: string;
  readonly min_qty: number;
  readonly max_qty: number | null;
  readonly discount_percent: number;
  readonly is_active: boolean;
}

export interface VolumeDiscountListResponse {
  readonly discounts: readonly VolumeDiscount[];
  readonly pagination: Pagination;
}

export interface VolumeDiscountListParams {
  readonly page?: number;
  readonly limit?: number;
}

export interface CreateVolumeDiscountInput {
  readonly name: string;
  readonly product_id: string;
  readonly min_qty: number;
  readonly max_qty: number | null;
  readonly discount_percent: number;
  readonly is_active: boolean;
}

// ---------------------------------------------------------------------------
// Import types
// ---------------------------------------------------------------------------

export interface B2BImportError {
  readonly row: number;
  readonly message: string;
}

export interface B2BImportResult {
  readonly total_rows: number;
  readonly imported: number;
  readonly skipped: number;
  readonly errors: readonly B2BImportError[];
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const b2bApi = {
  customers: {
    list: (params: B2BCustomerListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        search: params.search,
        region: params.region,
        status: params.status,
      });
      return adminFetch<B2BCustomerListResponse>(`/api/admin/b2b/customers${qs}`);
    },

    create: (input: CreateB2BCustomerInput) =>
      adminFetch<{ customer: B2BCustomer }>("/api/admin/b2b/customers", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    update: (id: string, input: UpdateB2BCustomerInput) =>
      adminFetch<{ customer: B2BCustomer }>(
        `/api/admin/b2b/customers/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body: JSON.stringify(input),
        },
      ),

    suspend: (id: string) =>
      adminFetch<{ customer: B2BCustomer }>(
        `/api/admin/b2b/customers/${encodeURIComponent(id)}/suspend`,
        { method: "PATCH" },
      ),

    activate: (id: string) =>
      adminFetch<{ customer: B2BCustomer }>(
        `/api/admin/b2b/customers/${encodeURIComponent(id)}/activate`,
        { method: "PATCH" },
      ),

    stats: () => adminFetch<B2BCustomerStats>("/api/admin/b2b/customers/stats"),
  },

  priceLists: {
    list: (params: PriceListListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        search: params.search,
      });
      return adminFetch<PriceListListResponse>(`/api/admin/b2b/price-lists${qs}`);
    },

    create: (input: CreatePriceListInput) =>
      adminFetch<{ price_list: PriceList }>("/api/admin/b2b/price-lists", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    items: (id: string) =>
      adminFetch<PriceListItemsResponse>(
        `/api/admin/b2b/price-lists/${encodeURIComponent(id)}/items`,
      ),
  },

  credit: {
    list: (params: CreditListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<CreditListResponse>(`/api/admin/b2b/credit${qs}`);
    },

    approve: (id: string) =>
      adminFetch<{ application: CreditApplication }>(
        `/api/admin/b2b/credit/${encodeURIComponent(id)}/approve`,
        { method: "PATCH" },
      ),

    reject: (id: string, notes: string) =>
      adminFetch<{ application: CreditApplication }>(
        `/api/admin/b2b/credit/${encodeURIComponent(id)}/reject`,
        {
          method: "PATCH",
          body: JSON.stringify({ notes }),
        },
      ),
  },

  invoices: {
    list: (params: B2BInvoiceListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        status: params.status,
        customer_id: params.customer_id,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<B2BInvoiceListResponse>(`/api/admin/b2b/invoices${qs}`);
    },
  },

  aging: {
    report: () => adminFetch<AgingReport>("/api/admin/b2b/aging"),
  },

  reps: {
    list: (params: SalesRepListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        search: params.search,
        region: params.region,
      });
      return adminFetch<SalesRepListResponse>(`/api/admin/b2b/reps${qs}`);
    },

    create: (input: CreateSalesRepInput) =>
      adminFetch<{ rep: SalesRep }>("/api/admin/b2b/reps", {
        method: "POST",
        body: JSON.stringify(input),
      }),
  },

  suppliers: {
    list: (params: SupplierListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        search: params.search,
        status: params.status,
        region: params.region,
      });
      return adminFetch<SupplierListResponse>(`/api/admin/b2b/suppliers${qs}`);
    },

    create: (input: CreateSupplierInput) =>
      adminFetch<{ supplier: Supplier }>("/api/admin/b2b/suppliers", {
        method: "POST",
        body: JSON.stringify(input),
      }),
  },

  volumeDiscounts: {
    list: (params: VolumeDiscountListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
      });
      return adminFetch<VolumeDiscountListResponse>(
        `/api/admin/b2b/volume-discounts${qs}`,
      );
    },

    create: (input: CreateVolumeDiscountInput) =>
      adminFetch<{ discount: VolumeDiscount }>("/api/admin/b2b/volume-discounts", {
        method: "POST",
        body: JSON.stringify(input),
      }),
  },

  import: {
    upload: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return adminFetch<B2BImportResult>("/api/admin/b2b/import", {
        method: "POST",
        body: form,
      });
    },
  },
} as const;
